import { useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Clock, Search, UserRound, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  approveProfileChangeApi,
  getProfileChangeRequestsApi,
  rejectProfileChangeApi,
} from "../../app/api";

const IMAGE_BASE = "https://uptask.visiontrek.io";

const FIELD_LABELS = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  phone_number: "Phone",
  address: "Address",
  current_address: "Current Address",
  permanent_address: "Permanent Address / Home Town",
  father_name: "Father Name",
  mother_name: "Mother Name",
  tenth_year: "10th Finished Year",
  twelfth_year: "12th Finished Year",
  graduation_level: "Graduation",
  graduation_course: "Graduation Course",
  graduation_year: "Graduation Year",
  post_graduation_level: "Post Graduation",
  post_graduation_course: "Post Graduation Course",
  post_graduation_year: "Post Graduation Year",
  profile_pic: "Profile Photo",
};

const currentFieldMap = {
  first_name: "current_first_name",
  last_name: "current_last_name",
  email: "current_email",
  phone_number: "current_phone_number",
  address: "current_address",
  current_address: "current_current_address",
  permanent_address: "current_permanent_address",
  father_name: "current_father_name",
  mother_name: "current_mother_name",
  tenth_year: "current_tenth_year",
  twelfth_year: "current_twelfth_year",
  graduation_level: "current_graduation_level",
  graduation_course: "current_graduation_course",
  graduation_year: "current_graduation_year",
  post_graduation_level: "current_post_graduation_level",
  post_graduation_course: "current_post_graduation_course",
  post_graduation_year: "current_post_graduation_year",
  profile_pic: "current_profile_pic",
};

export default function ApproveProfileChanges() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("PENDING");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadRequests = async () => {
    setLoading(true);
    const res = await getProfileChangeRequestsApi();
    setRequests(res.success ? res.data || [] : []);
    setLoading(false);
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const counts = useMemo(
    () => ({
      PENDING: requests.filter((request) => request.status === "PENDING").length,
      APPROVED: requests.filter((request) => request.status === "APPROVED").length,
    }),
    [requests]
  );

  const filteredRequests = useMemo(() => {
    const value = search.trim().toLowerCase();
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    return requests.filter((request) => {
      if (request.status !== activeTab) return false;

      const requestDate = new Date(request.reviewed_at || request.created_at).getTime();
      if (fromTime && requestDate < fromTime) return false;
      if (toTime && requestDate > toTime) return false;

      if (!value) return true;

      const name = `${request.current_first_name || ""} ${request.current_last_name || ""}`.toLowerCase();
      return (
        name.includes(value) ||
        String(request.emp_id || "").toLowerCase().includes(value) ||
        String(request.current_email || "").toLowerCase().includes(value) ||
        request.status.toLowerCase().includes(value)
      );
    });
  }, [requests, search, activeTab, dateFrom, dateTo]);

  const handleApprove = async (requestId) => {
    const res = await approveProfileChangeApi(requestId);
    if (res.success) {
      toast.success("Profile changes approved");
      loadRequests();
    } else {
      toast.error(res.message || "Unable to approve request");
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectReason.trim()) {
      toast.error("Please add a reject reason");
      return;
    }

    const res = await rejectProfileChangeApi(requestId, rejectReason.trim());
    if (res.success) {
      toast.success("Profile changes rejected");
      setRejectingId(null);
      setRejectReason("");
      loadRequests();
    } else {
      toast.error(res.message || "Unable to reject request");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pb-10 pt-28 md:pt-32">
      <section className="rounded-[2rem] border border-white/10 bg-[#13072f] text-white shadow-2xl overflow-hidden">
        <div className="border-b border-white/10 bg-gradient-to-br from-[#2f1a68] via-[#25104f] to-[#120628] p-6">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-cyan-400/15 text-cyan-200 ring-1 ring-cyan-200/30">
              <UserRound size={26} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-cyan-200/80">
                Admin review
              </p>
              <h1 className="text-2xl font-semibold">Approve Profile Changes</h1>
              <p className="text-sm text-white/60">
                Review employee edits before they become visible in the system.
              </p>
            </div>
          </div>

          <label className="flex h-12 min-w-[280px] items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4">
            <Search size={18} className="text-white/50" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, email, ID or status..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-white/40"
            />
          </label>
          </div>

          <div className="mt-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="inline-flex w-fit rounded-2xl border border-white/10 bg-black/20 p-1">
              <TabButton
                active={activeTab === "PENDING"}
                label="Pending Requests"
                count={counts.PENDING}
                onClick={() => setActiveTab("PENDING")}
              />
              <TabButton
                active={activeTab === "APPROVED"}
                label="Approved Requests"
                count={counts.APPROVED}
                onClick={() => setActiveTab("APPROVED")}
              />
            </div>

            <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100/70">
                <CalendarDays size={16} />
                Date Range
              </div>
              <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
              <DateInput label="To" value={dateTo} onChange={setDateTo} />
              <button
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="h-10 rounded-xl border border-white/15 px-4 text-sm text-white/70 hover:bg-white/10"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5 md:p-6">
          {loading && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/60">
              Loading requests...
            </div>
          )}

          {!loading && filteredRequests.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-12 text-center">
              <Clock className="mx-auto mb-3 text-white/40" size={34} />
              <h2 className="text-lg font-semibold">
                No {activeTab === "PENDING" ? "pending" : "approved"} profile changes found
              </h2>
              <p className="text-sm text-white/50">
                {activeTab === "PENDING"
                  ? "New employee requests will appear here."
                  : "Approved requests will move here after admin approval."}
              </p>
            </div>
          )}

          {!loading &&
            filteredRequests.map((request) => (
              <article
                key={request.id}
                className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-xl"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 overflow-hidden rounded-full bg-white/10">
                      {request.current_profile_pic ? (
                        <img
                          src={`${IMAGE_BASE}${request.current_profile_pic}`}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-sm font-semibold">
                          {(request.current_first_name || "?").charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <h2 className="font-semibold">
                        {request.current_first_name} {request.current_last_name}
                      </h2>
                      <p className="text-xs text-white/50">
                        ID {request.emp_id || "-"} • {request.current_email || "No email"}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                      request.status === "PENDING"
                        ? "bg-amber-400/15 text-amber-200"
                        : request.status === "APPROVED"
                        ? "bg-emerald-400/15 text-emerald-200"
                        : "bg-red-400/15 text-red-200"
                    }`}
                  >
                    {request.status === "PENDING" && <Clock size={14} />}
                    {request.status === "APPROVED" && <CheckCircle2 size={14} />}
                    {request.status === "REJECTED" && <XCircle size={14} />}
                    {request.status}
                  </span>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  {Object.entries(request.requested_changes || {}).map(([field, nextValue]) => (
                    <ChangeCard
                      key={field}
                      field={field}
                      currentValue={request[currentFieldMap[field]]}
                      nextValue={nextValue}
                    />
                  ))}
                </div>

                {request.status === "REJECTED" && request.admin_reason && (
                  <div className="mt-4 rounded-2xl border border-red-300/15 bg-red-400/10 p-3 text-sm text-red-100">
                    Reject reason: {request.admin_reason}
                  </div>
                )}

                {request.status === "PENDING" && (
                  <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                    {rejectingId === request.id && (
                      <input
                        value={rejectReason}
                        onChange={(event) => setRejectReason(event.target.value)}
                        placeholder="Reason for rejection..."
                        className="h-11 min-w-[260px] rounded-xl border border-white/15 bg-white/10 px-3 text-sm outline-none placeholder:text-white/40"
                      />
                    )}

                    {rejectingId === request.id ? (
                      <button
                        onClick={() => handleReject(request.id)}
                        className="rounded-xl bg-red-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20"
                      >
                        Confirm Reject
                      </button>
                    ) : (
                      <button
                        onClick={() => setRejectingId(request.id)}
                        className="rounded-xl border border-red-300/30 bg-red-400/10 px-5 py-3 text-sm font-semibold text-red-100"
                      >
                        Reject
                      </button>
                    )}

                    <button
                      onClick={() => handleApprove(request.id)}
                      className="rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20"
                    >
                      Approve Changes
                    </button>
                  </div>
                )}
              </article>
            ))}
        </div>
      </section>
    </div>
  );
}

function ValueBox({ label, value, highlight = false }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-cyan-400/10" : "bg-white/5"}`}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
      <p className="mt-1 break-words text-sm text-white">{value || "-"}</p>
    </div>
  );
}

function ChangeCard({ field, currentValue, nextValue }) {
  const isImage = field === "profile_pic";

  return (
    <div className="rounded-2xl border border-white/10 bg-black/15 p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40">
        {FIELD_LABELS[field] || field}
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {isImage ? (
          <>
            <ImageBox label="Current" value={currentValue} />
            <ImageBox label="Requested" value={nextValue} highlight />
          </>
        ) : (
          <>
            <ValueBox label="Current" value={currentValue} />
            <ValueBox label="Requested" value={nextValue} highlight />
          </>
        )}
      </div>
    </div>
  );
}

function ImageBox({ label, value, highlight = false }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-cyan-400/10" : "bg-white/5"}`}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
      <div className="mt-2 flex items-center gap-3">
        <div className="h-16 w-16 overflow-hidden rounded-2xl border border-white/15 bg-white/10">
          {value ? (
            <img src={`${IMAGE_BASE}${value}`} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full w-full place-items-center text-xs text-white/40">No photo</div>
          )}
        </div>
        <span className="break-all text-xs text-white/45">{value || "-"}</span>
      </div>
    </div>
  );
}

function TabButton({ active, label, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition ${
        active
          ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-lg shadow-cyan-500/20"
          : "text-white/60 hover:bg-white/10 hover:text-white"
      }`}
    >
      <span>{label}</span>
      <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-white/20" : "bg-white/10"}`}>
        {count}
      </span>
    </button>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <label className="flex h-10 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3">
      <span className="text-xs text-white/45">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="bg-transparent text-sm text-white outline-none [color-scheme:dark]"
      />
    </label>
  );
}
